/**
 * Centralized Export/Print Service
 * PDF + Print only — single HTML rendering pipeline for correct FR/AR layout.
 */

import { kvGet } from '../infrastructure/storage/kvAccess';
import printService from './printService';
import { getPrintExportBilingualPair } from '../utils/i18n/printExport';
import { enrichReportWithBusinessHeader } from '../utils/reportDocumentHeader';
import { buildReportDocumentHTML } from '../utils/reportDocumentBuilder';

class CentralizedExportPrintService {
  constructor() {
    this.isElectron = typeof window !== 'undefined' && window.electronAPI;
  }

  getCurrentLanguage() {
    const savedLanguage = kvGet('appLanguage');
    const s = savedLanguage == null ? '' : String(savedLanguage);
    if (s === 'ar' || s === 'fr') return s;
    if (typeof document !== 'undefined') {
      const docLang = document.documentElement?.lang || '';
      if (docLang.startsWith('ar')) return 'ar';
      if (docLang.startsWith('fr')) return 'fr';
      if (document.documentElement?.dir === 'rtl') return 'ar';
    }
    return 'fr';
  }

  resolveExportLanguage(options = {}) {
    const lang = options.lang || options.language || this.getCurrentLanguage();
    return lang === 'ar' ? 'ar' : 'fr';
  }

  getBilingualLabels(key) {
    return getPrintExportBilingualPair(key);
  }

  /**
   * @deprecated Kept for backward compatibility — normalization happens in reportDocumentBuilder.
   */
  prepareBilingualData(data) {
    return data;
  }

  async buildReportHtml(data, options = {}) {
    const lang = this.resolveExportLanguage(options);
    const enriched = await enrichReportWithBusinessHeader({ ...data, exportLang: lang });
    return buildReportDocumentHTML(enriched, { lang });
  }

  /**
   * Export report as PDF (HTML → PDF).
   * @param {Object} data
   * @param {'pdf'} [format]
   * @param {Object} [options]
   */
  async exportBilingual(data, format = 'pdf', options = {}) {
    const lang = this.resolveExportLanguage(options);

    if (format.toLowerCase() !== 'pdf') {
      return {
        success: false,
        error: `Unsupported format: ${format}`,
        message: this.getBilingualLabels('export_failed')[lang]
      };
    }

    try {
      const html = await this.buildReportHtml(data, options);
      const title = lang === 'ar'
        ? (data.titleAr || data.title || 'تقرير')
        : (data.titleFr || data.title || 'Rapport');
      const filename = options.filename || `${String(title).replace(/\s+/g, '_')}.pdf`;

      if (this.isElectron) {
        const pdfResult = await this.tryExportPdfViaElectron(html, filename);
        if (pdfResult) {
          return pdfResult;
        }
        return await this.exportPdfViaPrintDialog(html, filename, lang);
      }

      return await this.downloadPdfInBrowser(html, filename, lang);
    } catch (error) {
      console.error('[Export] PDF export failed:', error);
      return {
        success: false,
        error: error.message,
        message: this.getBilingualLabels('export_failed')[lang]
      };
    }
  }

  async tryExportPdfViaElectron(html, filename) {
    const lang = this.getCurrentLanguage();
    const successPayload = (result) => ({
      success: true,
      filename: result.filename || filename,
      path: result.path,
      message: this.getBilingualLabels('export_success')[lang]
    });

    const channels = [
      async () => {
        if (!window.electronAPI?.htmlToPDF) return null;
        return window.electronAPI.htmlToPDF({ html, filename });
      },
      async () => {
        if (!window.electronAPI?.printWithDialog) return null;
        return window.electronAPI.printWithDialog(html, {
          exportPdf: true,
          filename,
          printBackground: true
        });
      }
    ];

    for (const invoke of channels) {
      if (!invoke) continue;
      try {
        const result = await invoke();
        if (result?.success) {
          return successPayload(result);
        }
        if (result && result.success === false && result.error) {
          console.warn('[Export] PDF channel failed:', result.error);
        }
      } catch (error) {
        const message = String(error?.message || error);
        if (
          message.includes('No handler registered') ||
          message.includes('htmlToPDF')
        ) {
          continue;
        }
        throw error;
      }
    }

    return null;
  }

  async exportPdfViaPrintDialog(html, filename, lang) {
    const printResult = await printService.printWithDialog(html, {
      forceDialog: true,
      printBackground: true,
      margins: { marginType: 'printableArea' }
    });
    if (!printResult?.success) {
      return {
        success: false,
        error: printResult?.error,
        message: this.getBilingualLabels('export_failed')[lang]
      };
    }
    return {
      success: true,
      filename,
      message: lang === 'ar'
        ? 'استخدم "حفظ كـ PDF" في نافذة الطباعة'
        : 'Utilisez « Enregistrer au format PDF » dans la fenêtre d\'impression'
    };
  }

  async downloadPdfInBrowser(html, filename, lang) {
    if (this.isElectron && window.electronAPI?.printWithDialog) {
      const result = await printService.printWithDialog(html, {
        forceDialog: true,
        printBackground: true,
        margins: { marginType: 'printableArea' }
      });
      return {
        success: Boolean(result?.success),
        filename,
        error: result?.error,
        message: result?.success
          ? (lang === 'ar'
            ? 'استخدم "حفظ كـ PDF" في نافذة الطباعة'
            : 'Utilisez « Enregistrer au format PDF » dans la fenêtre d\'impression')
          : this.getBilingualLabels('export_failed')[lang]
      };
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      throw new Error('Popup blocked — allow popups to export PDF');
    }

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();

    await new Promise((resolve) => {
      printWindow.onload = resolve;
      setTimeout(resolve, 500);
    });

    printWindow.focus();
    printWindow.print();

    return {
      success: true,
      filename,
      message: this.getBilingualLabels('export_success')[lang]
    };
  }

  /**
   * Print report / receipt / invoice.
   */
  async printBilingual(data, type = 'report', options = {}) {
    const lang = this.resolveExportLanguage(options);

    try {
      if (type === 'report') {
        const html = await this.buildReportHtml(data, options);
        return await printService.printWithDialog(html, {
          kind: 'document',
          printBackground: true,
          margins: { marginType: 'none' },
          timeoutMs: 10000,
          retries: 1
        });
      }

      if (type === 'receipt') {
        return await printService.printReceipt(data);
      }

      if (type === 'invoice') {
        return await printService.printInvoice(data);
      }

      throw new Error(`Unknown print type: ${type}`);
    } catch (error) {
      console.error('[Export] Print failed:', error);
      return {
        success: false,
        error: error.message,
        message: this.getBilingualLabels('print_failed')[lang]
      };
    }
  }
}

const centralizedExportPrintService = new CentralizedExportPrintService();
export default centralizedExportPrintService;
